import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { FadeUp, MaskedLineReveal } from "./motion/Reveal";
import { TEST_IDS } from "../constants/testIds";
import { api } from "../lib/api";
import { Camera } from "lucide-react";

export function Cantieri() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/cantieri", { withCredentials: false })
      .then((r) => alive && setItems(r.data || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="cantieri"
      data-testid={TEST_IDS.cantieri.section}
      className="relative py-28 md:py-44 border-t border-white/10 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24 items-end">
          <div className="col-span-12 md:col-span-7">
            <FadeUp>
              <span className="eyebrow">
                <span className="inline-block mr-3 w-6 h-px bg-[#E31E24] align-middle" />
                Cantieri · In corso
              </span>
            </FadeUp>
            <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              <MaskedLineReveal>Dal cantiere,</MaskedLineReveal>
              <MaskedLineReveal delay={0.15}>
                <span className="font-editorial italic font-light">in tempo</span> reale.
              </MaskedLineReveal>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <FadeUp delay={0.2}>
              <p className="text-white/60 leading-relaxed">
                Immagini caricate direttamente dai nostri tecnici in cantiere.
                Ogni foto racconta un passaggio: la posa, la prova di tenuta, il
                collaudo, la finitura. Un archivio vivo dei nostri lavori.
              </p>
            </FadeUp>
          </div>
        </div>

        {loading ? (
          <SkeletonGrid />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            data-testid={TEST_IDS.cantieri.grid}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10"
          >
            {items.map((it, i) => (
              <CantiereCard key={it.id} item={it} index={i} />
            ))}
          </div>
        )}
      </div>

      <div className="mt-24 md:mt-36 border-y border-white/10 py-6 bg-[#0A0C10] overflow-hidden w-full">
        <Marquee gradient={false} speed={40}>
          <div className="marquee-track font-editorial italic text-white/70 text-3xl md:text-5xl lg:text-6xl mr-16">
            <span>Foxtherm</span>
            <span className="text-[#E31E24]">—</span>
            <span>Impianti Radianti</span>
            <span className="text-[#E31E24]">—</span>
            <span>Impianti Idrosanitari</span>
            <span className="text-[#E31E24]">—</span>
            <span>Termici</span>
            <span className="text-[#E31E24]">—</span>
            <span>HVAC</span>
            <span className="text-[#E31E24]">—</span>
          </div>
        </Marquee>
      </div>
    </section>
  );
}

function CantiereCard({ item, index }) {
  return (
    <motion.article
      data-testid={TEST_IDS.cantieri.item(item.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: (index % 6) * 0.05, ease: [0.76, 0, 0.24, 1] }}
      className="group relative bg-[#0A0C10] aspect-[4/5] overflow-hidden spotlight-frame"
    >
      <img
        src={item.image_url}
        alt={item.title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-[#0A0C10]/30 to-transparent" />
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F03B41] mb-2">
          {item.location || "In corso"}
        </div>
        <h3 className="font-display font-black uppercase text-xl md:text-2xl tracking-tight leading-tight">
          {item.title}
        </h3>
        {item.description ? (
          <p className="mt-2 text-white/70 text-sm max-w-md line-clamp-3">
            {item.description}
          </p>
        ) : null}
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
        {new Date(item.created_at).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </div>
    </motion.article>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="aspect-[4/5] bg-[#12151C] relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent animate-[shimmer_2s_infinite]" />
        </div>
      ))}
      <style>{`@keyframes shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }`}</style>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid={TEST_IDS.cantieri.empty}
      className="border border-white/10 p-14 md:p-20 flex flex-col items-center text-center bg-[#12151C]"
    >
      <div className="w-14 h-14 border border-[#E31E24] flex items-center justify-center mb-6">
        <Camera size={22} className="text-[#E31E24]" />
      </div>
      <h3 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">
        Nuove immagini in arrivo
      </h3>
      <p className="mt-3 text-white/60 max-w-md">
        Stiamo caricando le prime foto dai cantieri in corso. I nostri tecnici
        aggiornano questa galleria direttamente dal telefono, in tempo reale.
      </p>
    </div>
  );
}
